import { Form, Formik } from 'formik';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import ModalBody from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import CustomTextArea from '../../../components/text-area/textArea';
import {
  useGetFeaturesPermissionsDistributable,
  useWealthlaneIdentityApi,
} from '../../../hooks';
import useGetFeaturePermissionSet from '../../../hooks/get/useGetFeaturePermissionSet';
import { type ModalStatusType } from '../../../types/common';
import { FEATURE_SET_VALIDATION, ROLES_VALIDATION } from '../../../utils/validations/validations';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import { SORTED_FIELD_PERMISSION, SORTED_MODULE_PERMISSION } from '../../../constants/common';
import { handleFilterPermissionList, handleSortPermissionNames } from '../../../utils/global';
import { convertDualPermissions, convertPermissions } from '../../../utils/permission/permissionAdapter';
import { getFilteredPermissionData } from '../../../utils';
import {
  PermissionMatrixForm,
  useFilteredPermissionMatrix,
  PERMISSION_MODAL_TOOLTIP,
  getPermissionModalTitle,
  type PermissionChangeLog,
} from '../shared/permission-matrix';
import { usePostAccessProfile } from './useAccessProfileHooks';
import { type AccessProfileScope, ACCESS_PROFILE_CONFIG } from './types';

interface AccessProfileModalProps {
  id?: string;
  scope: AccessProfileScope;
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  loading?: boolean;
}

const AccessProfileModal = ({
  id,
  scope,
  isModalOpen,
  closeModal,
  initialData,
  setModalStatus,
  modalStatus,
  handleRequest,
  loading,
}: AccessProfileModalProps): JSX.Element => {
  const config = ACCESS_PROFILE_CONFIG[scope];
  const { mutate, isLoading } = usePostAccessProfile(scope, handleRequest, closeModal, initialData.id);
  const userAuthList = useSelector(selectAuthRoutesList);
  const allFieldValues = userAuthList.localization?.values?.Wealthlane;
  const [manualSearch, setManualSearch] = useState('');

  const permissionChangeLogs = initialData.data?.permissionChangeLogs as
    | PermissionChangeLog[]
    | null
    | undefined;

  const { handleRequest: handleRequestIdentity } = useWealthlaneIdentityApi();
  const { data: distributablePermissions } = useGetFeaturesPermissionsDistributable(
    handleRequestIdentity,
    scope === 'FeatureSet'
  );
  const { data: newPermissions } = useGetFeaturePermissionSet(handleRequest, scope === 'Role');

  const [moduleLevelPermissions, setModuleLevelPermissions] = useState<any[]>([]);
  const [fieldLevelPermissionsList, setFieldLevelPermissionsList] = useState<any[]>([]);

  useEffect(() => {
    if (scope === 'FeatureSet' && distributablePermissions?.fieldLevelPermissions) {
      setModuleLevelPermissions(distributablePermissions?.permissions ?? []);
      setFieldLevelPermissionsList(distributablePermissions?.fieldLevelPermissions ?? []);
    }
  }, [distributablePermissions, scope]);

  const { nonFieldLevelPermissionsGroups, fieldLevelPermissionsGroups } =
    scope === 'FeatureSet'
      ? (() => {
        const moduleGroups = getFilteredPermissionData(
          convertDualPermissions(moduleLevelPermissions),
          'CustomerService.Customer.',
          allFieldValues
        );
        const fieldGroups = getFilteredPermissionData(
          convertDualPermissions(fieldLevelPermissionsList),
          'CustomerService.Customer.',
          allFieldValues
        );
        return {
          nonFieldLevelPermissionsGroups: moduleGroups.nonFieldLevelPermissionsGroups,
          fieldLevelPermissionsGroups: fieldGroups.fieldLevelPermissionsGroups,
        };
      })()
      : (() => {
        const result = getFilteredPermissionData(
          convertPermissions(newPermissions ?? []),
          'CustomerService.Customer.',
          allFieldValues
        );
        const filteredFieldGroups: Record<string, any[]> = {};
        Object.entries(result.fieldLevelPermissionsGroups).forEach(([groupKey, items]: [string, any]) => {
          const customerOnly = items.filter(
            (item: any) => !item.key.startsWith('CustomerService.Vendor.')
          );
          if (customerOnly.length > 0) {
            filteredFieldGroups[groupKey] = customerOnly;
          }
        });
        return {
          nonFieldLevelPermissionsGroups: result.nonFieldLevelPermissionsGroups,
          fieldLevelPermissionsGroups: filteredFieldGroups,
        };
      })();

  const filterNonFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(nonFieldLevelPermissionsGroups),
    SORTED_MODULE_PERMISSION
  );
  const filterFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(fieldLevelPermissionsGroups),
    SORTED_FIELD_PERMISSION
  );

  const { filteredData, filteredFieldLevelData } = useFilteredPermissionMatrix({
    filterNonFieldLevelPermission,
    filterFieldLevelPermission,
    manualSearch,
    allFieldValues,
  });

  const handleCheckedAll = (
    key: string,
    setFieldValue: any,
    dataList: any,
    wrapperName: string,
    isCheckedAll: boolean
  ): void => {
    const allFields = handleFilterPermissionList(dataList)[key];
    const filterAllDisabledData = allFields.filter(
      (item: any) => !(item.isDisabled as boolean) && item
    );

    if (isCheckedAll) {
      allFields.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, false)
      );
    } else {
      filterAllDisabledData.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, true)
      );
    }
  };

  const handleSubmit = (values: any): void => {
    if (scope === 'Role') {
      delete values.permissions['CustomerService-Customer'];
      delete values.fieldLevelPermissions['CustomerService-Customer-FieldLevel'];
    }

    const permissions = Object.keys(values?.permissions)
      .filter((x) => values.permissions[x] !== false)
      .map((item) => item.replaceAll('-', '.'));
    const fieldLevelPermissions = Object.keys(values?.fieldLevelPermissions ?? {})
      .filter((x) => values.fieldLevelPermissions[x] !== false)
      .map((item) => item.replaceAll('-', '.'));

    const payload = {
      name: scope === 'FeatureSet' ? values.name : values.roleName,
      description: values.description,
      type: scope,
      permissions: scope === 'FeatureSet' ? permissions.concat(fieldLevelPermissions) : permissions,
      fieldLevelPermissions: scope === 'Role' ? fieldLevelPermissions : undefined,
      isEnabled: scope === 'Role' ? values.isEnabled : undefined,
    };

    mutate(payload);
  };

  const validationSchema = scope === 'FeatureSet' ? FEATURE_SET_VALIDATION : ROLES_VALIDATION;
  const entityNameValue =
    scope === 'FeatureSet' ? initialData.data.name : initialData.data.roleName;
  const entityType = scope === 'FeatureSet' ? 'featureSet' : 'role';

  return (
    <Formik
      enableReinitialize
      initialValues={initialData.data}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({ touched, handleChange, errors, values, setFieldValue }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            loading={loading}
            size={modalStatus !== 'Add' ? '3xl' : 'lg'}
            title={getPermissionModalTitle(modalStatus, entityType, entityNameValue)}
            valueEdited={_.isEqual(values, initialData.data)}
            id={id}
            showToolTip={true}
            tooltipText={PERMISSION_MODAL_TOOLTIP}
            children={
              <Form id={`accessProfile${scope}Form`}>
                <ModalBody id={`accessProfile${scope}ModalBody`}>
                  <div className='grid sm:grid-cols-4 form-gap'>
                    <div className='sm:col-span-3'>
                      <InputText
                        type='text'
                        label={config.nameLabel}
                        name={scope === 'FeatureSet' ? 'name' : 'roleName'}
                        id={scope === 'FeatureSet' ? 'name' : 'roleName'}
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                    </div>
                    <div className='sm:col-span-3'>
                      <CustomTextArea
                        label='Description'
                        name='description'
                        id='description'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        rows='5'
                      />
                    </div>
                  </div>

                  <PermissionMatrixForm
                    variant='single'
                    modalStatus={modalStatus}
                    values={values}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    allFieldValues={allFieldValues}
                    filteredData={filteredData}
                    filteredFieldLevelData={filteredFieldLevelData}
                    nonFieldLevelPermissionsGroups={nonFieldLevelPermissionsGroups}
                    fieldLevelPermissionsGroups={fieldLevelPermissionsGroups}
                    filterNonFieldLevelPermission={filterNonFieldLevelPermission}
                    filterFieldLevelPermission={filterFieldLevelPermission}
                    manualSearch={manualSearch}
                    onSearchChange={setManualSearch}
                    searchInputId={`search${scope}Input`}
                    handleCheckedAll={handleCheckedAll}
                    permissionChangeLogs={permissionChangeLogs}
                  />
                </ModalBody>
                <ModalFooterComponent
                  id={`accessProfile${scope}ModalFooter`}
                  modalStatus={modalStatus}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  isEditShow={
                    scope === 'Role'
                      ? (initialData.data.isEditable as boolean) || false
                      : true
                  }
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  urlName={config.urlName}
                  valueChanged={_.isEqual(values, initialData.data)}
                  loading={isLoading}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default AccessProfileModal;
